import { isPositiveTrigger } from './grading-mode.js';
import { NO_SENSOR_DATA_TRIGGER } from './sensor-health.js';
/**
 * The SLOT an effect occupies, for deciding what a card may not hold twice.
 *
 * Narrower than the effect's own identity on purpose. Two `setVisibility` rows naming different
 * modes are not duplicates — they are a contradiction, and resolving it by "later wins" would hide
 * an authoring mistake rather than prevent it. So visibility slots per BLOCK, while a flag slots
 * per block and field, where two rows genuinely are the same switch.
 *
 * Scoped to one card by its caller, never across the whole Changes list: two changes touching the
 * same setting under different conditions is the case `FlowChange.title` exists for.
 */
export function effectSlot(effect) {
    return effect.kind === 'setFlag'
        ? `setFlag:${effect.blockId}:${effect.field}`
        : `${effect.kind}:${effect.blockId}`;
}
export const EMPTY_SENSOR_RUNG = { action: { type: 'stay' }, split: false, rules: [] };
export const CLEAR_ON_ARRIVAL = { answers: false, effects: false };
export const DEFAULT_ARRIVAL = { keep: CLEAR_ON_ARRIVAL, split: false, rules: [] };
/**
 * What this block does on a return. The ONE place the split is resolved.
 *
 * An absent policy is the default, which is what every task authored before this existed has and
 * what a task nobody can return to never consults.
 */
export function arrivalKeepFor(policy, blockId) {
    if (!policy)
        return CLEAR_ON_ARRIVAL;
    if (!policy.split)
        return policy.keep;
    const rule = policy.rules.find((r) => r.blockId === blockId);
    // A block with no row of its own follows the consolidated answer rather than the default: the
    // top-level setting is what the teacher last said about "everything", and a block added after
    // they split should not silently disagree with its neighbours.
    return rule ? { answers: rule.answers, effects: rule.effects } : policy.keep;
}
/** The slot's key, for anything stored against it. Same device as `effectSlot`. */
export function flowSlotId(slot) {
    return slot.ref ? `${slot.kind}:${slot.ref}` : slot.kind;
}
/**
 * The inverse, for a key that has travelled — a stored exit's own map key, or a URL sending a
 * teacher to the summary feedback a problems panel flagged.
 *
 * Here beside `flowSlotId` so the two cannot drift: split on the FIRST colon only, since a `ref`
 * is a rule or block id and nothing stops one containing another.
 */
export function parseFlowSlotId(id) {
    const cut = id.indexOf(':');
    return cut < 0
        ? { kind: id }
        : { kind: id.slice(0, cut), ref: id.slice(cut + 1) };
}
/**
 * WHAT A STUDENT READS ON THEIR WAY OUT, authored per exit.
 *
 * Only an EXIT can carry it -- `stay` keeps them on the task, so there is nothing to say
 * goodbye with. The content is kept against the slot rather than on the action, because the
 * recorded attempt stores the outcome and an action carrying images would put a copy of them
 * into every attempt in the log.
 */
export const EXIT_BLOCK_TYPES = ['text', 'image', 'sound'];
/**
 * Five, which is text / image / text / image / text.
 *
 * The cap belongs to the AUTHORING surface, never to the reader: a stored exit holding six
 * renders six and lets the teacher delete down. Truncating authored work to satisfy a limit is
 * much the worse failure.
 */
export const EXIT_BLOCK_CAP = 5;
/** Whether this action LEAVES the task, which is the whole condition for carrying exit content. */
export function isExitAction(action) {
    return !!action && action.type !== 'stay';
}
export const DEFAULT_FLOW_CONFIG = {
    noAnswerAction: { type: 'stay' },
    noSensorData: EMPTY_SENSOR_RUNG,
    poorSensorData: EMPTY_SENSOR_RUNG,
    eventRules: [],
    defaultAction: { type: 'stay' },
    allCorrectAction: { type: 'next' },
    changes: [],
};
/**
 * The STUDENT did not answer. Drives Any Incomplete.
 *
 * Split out of what used to be one `RETRY_TRIGGERS` set holding these and the sensor failures
 * below. They are not the same thing and they no longer want the same rung: a blank is the student
 * choosing not to answer yet, a bad fix is the hardware, and a teacher wants to respond to them
 * differently. Making a blank acceptable is an evaluation decision — `gradingMode: 'optional'` —
 * not a flow one.
 */
const INCOMPLETE_TRIGGERS = new Set(['unanswered', 'incomplete_answer']);
/**
 * The device produced NO reading at all — dead, denied, or unsupported. Drives No Sensor Data.
 *
 * One trigger, not one per cause: the causes differ in what to tell the student, which the block's
 * own live notice already does, but not in what the teacher can do about it.
 */
const NO_DATA_TRIGGERS = new Set([NO_SENSOR_DATA_TRIGGER]);
/**
 * The device produced a reading that is not good enough to grade. Drives Poor Sensor Data.
 *
 * Split from the no-data set above because the REMEDIES differ and a rung carries one action: no
 * data merits a redirect or unlocking a manual bypass, poor data merits staying put and trying
 * again. One rung could not serve both.
 */
const POOR_DATA_TRIGGERS = new Set(['poor_accuracy', 'poor_heading_accuracy', 'poor_position_accuracy', 'poor_axis']);
export function isIncompleteTrigger(trigger) {
    return INCOMPLETE_TRIGGERS.has(trigger);
}
export function isNoSensorDataTrigger(trigger) {
    return NO_DATA_TRIGGERS.has(trigger);
}
export function isPoorSensorDataTrigger(trigger) {
    return POOR_DATA_TRIGGERS.has(trigger);
}
/** Either kind of device failure. For callers that only ask "is this the hardware's fault". */
export function isSensorTrigger(trigger) {
    return NO_DATA_TRIGGERS.has(trigger) || POOR_DATA_TRIGGERS.has(trigger);
}
/** Either kind: no usable answer, whatever the reason. Kept for callers that need the old union. */
export function isRetryTrigger(trigger) {
    return INCOMPLETE_TRIGGERS.has(trigger) || isSensorTrigger(trigger);
}
/** Pulls the acknowledged evaluation out of a conditionAcknowledged signature — only
 * `active:<evaluation>` signatures carry one; `inactive:...`/`deleted` have no evaluation to
 * compare a live firing against (a state that was inactive or gone couldn't have been firing
 * when it was last acknowledged, so there's nothing for a NEW firing to have drifted from).
 * Tolerates an optional `:<branchScopeKey>` suffix (teacher-interface's getConditionSignature
 * appends one for a block-branch-collapsed condition) — the evaluation is always the first
 * captured group regardless of whether a scope suffix follows. */
function acknowledgedEvaluation(signature) {
    const match = /^active:(correct|incorrect|neutral)(?::.+)?$/.exec(signature);
    return match ? match[1] : undefined;
}
const FLOW_GROUP_PREFIX = 'flowgroup';
/** Every real feedback-state id in this app is a bare `crypto.randomUUID()` (or, for some
 * targeted triggers, several such uuids joined with underscores) — never a colon. A synthetic
 * group key always contains colons, so collision with a real id is structurally impossible, not
 * just unlikely, as long as that invariant holds. */
export function buildFlowGroupId(key) {
    return `${FLOW_GROUP_PREFIX}:${key.type}:${key.evaluation}:${key.trigger}`;
}
const FLOW_GROUP_RE = /^flowgroup:(default|targeted):(neutral|correct|incorrect):([\s\S]+)$/;
export function parseFlowGroupId(id) {
    const match = FLOW_GROUP_RE.exec(id);
    return match ? { type: match[1], evaluation: match[2], trigger: match[3] } : undefined;
}
/** The one place both resolveFlow (real fired-state ids) and teacher-interface's flow-utils
 * (synthetic collapsed-group ids) decide whether a stored condition id refers to a fired state.
 * Literal id equality first — byte-identical to matching a plain `===`, so a non-branching block
 * (or any block before this existed) sees zero behavior change. Only when `id` doesn't match
 * directly AND parses as a synthetic group key does it fall back to a (type, trigger, evaluation)
 * match — this is what lets one collapsed Flow condition match whichever branch's copy of that
 * trigger actually fired, without resolveFlow needing to know anything about branches itself. */
export function stateMatchesConditionId(state, id) {
    if (state.id === id)
        return true;
    const key = parseFlowGroupId(id);
    return key !== undefined && state.type === key.type && state.trigger === key.trigger && state.evaluation === key.evaluation;
}
const NO_ATTEMPTS = { all: 0, complete: 0, unique: 0, incorrect: 0 };
const NO_WINDOW = { task: NO_ATTEMPTS, blocks: {} };
const NO_COUNTS = { taskId: '', visit: NO_WINDOW, session: NO_WINDOW, visits: {} };
function tallyFor(subject, window) {
    return subject.scope === 'task' ? window.task : window.blocks[subject.blockId] ?? NO_ATTEMPTS;
}
function comparePasses(n, c) {
    switch (c.op) {
        case 'atLeast': return n >= c.n;
        case 'atMost': return n <= c.n;
        case 'exactly': return n === c.n;
        // Strictly redundant on an integer count — `> 5` is `>= 6` — and offered anyway, because a
        // teacher who means "more than five" should be able to write that rather than translate it,
        // and a rule that reads the way it was thought is one less thing to re-derive later.
        case 'moreThan': return n > c.n;
        case 'fewerThan': return n < c.n;
        // Inclusive at both ends, and tolerant of a reversed pair: a teacher who types 9 then 5 means
        // the same range, and an authoring slip should not silently make a rule unmatchable.
        case 'between': return n >= Math.min(c.lo, c.hi) && n <= Math.max(c.lo, c.hi);
    }
}
/** Whether one condition holds for this submission. The only place a condition kind is read. */
function conditionHolds(condition, blockResults, counts) {
    if (condition.kind === 'feedback') {
        return (blockResults[condition.blockId] ?? []).some((s) => stateMatchesConditionId(s, condition.stateId));
    }
    if (condition.kind === 'visits') {
        // A task never entered is zero, not unmatchable: "fewer than 2 visits" is true on a task the
        // student has not reached, and a teacher asking that means it.
        return comparePasses(counts.visits[condition.taskId ?? counts.taskId] ?? 0, condition.compare);
    }
    const window = counts[condition.within ?? 'visit'];
    return comparePasses(tallyFor(condition.subject, window)[condition.filter], condition.compare);
}
/**
 * Whether a list of conditions is satisfied, per matchMode.
 *
 * An EMPTY list never matches, under either mode. `every` over `[]` is vacuously true, which would
 * make a freshly-added, not-yet-configured rule fire on every submission — the worst possible
 * default for a rule that routes.
 */
/**
 * A RULE'S CONDITIONS, UNDER EITHER SHAPE.
 *
 * They were stored as a nested `Record<blockId, stateId[]>` and are a flat list now. The teacher
 * app migrates on load, so a task it has opened and saved sends the new shape -- but the engine
 * is ALSO handed raw stored flows by surfaces that never normalize: the Preview panel and the
 * connections diagram both derive from `experience.tasks` directly.
 *
 * Five tasks across four real experiences still hold the nested shape, and `conditions.every`
 * on an object threw: opening any of them in Preview rendered "Something went wrong" instead of
 * the task -- no panel, no Submit. The DTO already says this field accepts either spelling; this
 * is the reader keeping that promise.
 */
function asConditionList(raw) {
    if (Array.isArray(raw))
        return raw;
    if (!raw || typeof raw !== 'object')
        return [];
    const out = [];
    for (const [blockId, ids] of Object.entries(raw)) {
        if (!Array.isArray(ids))
            continue;
        for (const stateId of ids) {
            if (typeof stateId === 'string')
                out.push({ kind: 'feedback', blockId, stateId });
        }
    }
    return out;
}
function conditionsSatisfied(rawConditions, matchMode, blockResults, counts) {
    const conditions = asConditionList(rawConditions);
    if (conditions.length === 0)
        return false;
    const holds = (c) => conditionHolds(c, blockResults, counts);
    return matchMode === 'any' ? conditions.some(holds) : conditions.every(holds);
}
/**
 * Whether a rule's meaning has drifted since a teacher last confirmed it.
 *
 * A condition's targeted state can be reclassified (its evaluation changed) without being deleted
 * or deactivated — see `conditionAcknowledged`. If the live evaluation of a condition that is
 * actually firing no longer matches what was acknowledged, the rule is skipped entirely rather than
 * acted on, falling through exactly like a rule that never matched.
 */
function hasDrifted(rule, blockResults) {
    /* `asConditionList`: a rule from a document nobody has re-saved still holds the nested shape. */
    return asConditionList(rule.conditions).some((c) => {
        if (c.kind !== 'feedback')
            return false;
        const signature = rule.conditionAcknowledged?.[c.blockId]?.[c.stateId];
        if (signature === undefined)
            return false;
        const acknowledgedEval = acknowledgedEvaluation(signature);
        if (acknowledgedEval === undefined)
            return false;
        const fired = (blockResults[c.blockId] ?? []).find((s) => stateMatchesConditionId(s, c.stateId));
        return fired !== undefined && fired.evaluation !== acknowledgedEval;
    });
}
/**
 * Every effect that applies to this submission. The CHANGES pass.
 *
 * All matching rules apply, in list order, and a later effect wins over an earlier one touching the
 * same thing. Runs independently of `resolveFlow`: effects have no destination, so there is nothing
 * for them to compete with, and keeping them out of the navigation ladder is what stops an
 * unbounded effect rule shadowing every route below it.
 */
export function resolveChanges(config, blockResults, counts = NO_COUNTS) {
    return (config.changes ?? [])
        /*
         * MALFORMED ENTRIES ARE SKIPPED, because the database holds some.
         *
         * The list is stored loosely on purpose -- its contents are authored data the server has no
         * opinion about -- and for a while the API destroyed it on the way in: `enableImplicitConversion`
         * coerced each Change OBJECT to its reflected type and wrote `[]` in its place. The DTO names
         * `@Type(() => Object)` to stop that now, and the corpses are still in real documents: two
         * tasks in one experience hold `changes: [[]]`.
         *
         * Reading one used to THROW -- `c.conditions` on an array is `undefined`, and
         * `conditionsSatisfied` reads `.length` off it -- which took down the whole submission, not
         * just the Change. A reader of deliberately loose data has to tolerate junk in it, so an entry
         * that is not an object with a condition list is dropped and the rest of the list still runs.
         */
        .filter((c) => !!c && typeof c === 'object' && !Array.isArray(c))
        .filter((c) => conditionsSatisfied(c.conditions ?? [], c.matchMode, blockResults, counts))
        .flatMap((c) => c.effects ?? []);
}
export function flowEventRules(flow) {
    return flow.eventRules ?? flow.incorrectRules ?? [];
}
export function flowDefaultAction(flow) {
    return flow.defaultAction ?? flow.incorrectDefaultAction ?? { type: 'stay' };
}
/**
 * Resolves what a "Proceed" click should do, given each block's already-computed FeedbackState[].
 * Never calls evaluate() itself — stays decoupled from per-block evaluation logic.
 *
 * ## The ladder, and why All Correct sits where it does
 *
 * `Any Incomplete → Sensor Problem → All Correct → Events → Default`, first match wins.
 *
 * All Correct used to come LAST, after the events, and that made an attempt-only event a trap: once
 * "attempts >= 5" was true it was true forever, so the task could never reach All Correct and a
 * student who finally got it right was routed away anyway. Moving All Correct above the events
 * makes correct an escape hatch nothing can shadow, and mirrors the order every block editor
 * already presents its feedback in (No Answer, Correct, Targeted, Default Incorrect).
 *
 * ## All Correct is a POSITIVE test: every block passed
 *
 * It used to be "nothing incorrect", and that let a neutral through as a free pass. The owner's
 * rule: *"neutrals don't hurt you if you are right, but don't help you if you are wrong."* That
 * is already how a multi-select MCQ grades its options — selecting neutrals neither helps nor
 * hurts, selecting ONLY neutrals is not correct — and this is the same rule one level up, over
 * blocks instead of options.
 *
 * So All Correct fires when every block fired its own mode's pass state: `correct` in graded,
 * `any_answer` in survey, `any_response` in optional. A block that fired a targeted state did
 * not pass, whatever its evaluation says — and in a participation mode a targeted state is
 * forcibly neutral, so "which neutrals count" has a crisp answer: none of them.
 *
 * ## Which is why there is no longer a split carve-out
 *
 * A `splitParticipationBlockIds` parameter used to suppress All Correct for a survey block with
 * targeted states authored, because under the negative test such a block passed on a neutral and
 * its Special Events never got a chance. Under the positive test it falls out: a fired targeted
 * state is not a pass state, All Correct does not match, and the events run. The inference, the
 * parameter, and the "Disabled by targeted feedback" note it drove all go.
 *
 * One case genuinely changes with it. A survey where only SOME options are targeted, answered on
 * an untargeted one, fires `any_answer` — a pass — and takes the All Correct route where the
 * carve-out sent it to the events. That is the right reading: the teacher left those options
 * untargeted on purpose, and a Special Event on `any_answer` can still route them, which is why
 * the positive slot had to become offerable first.
 */
export function resolveFlow(config, blockResults, counts = NO_COUNTS) {
    return resolveFlowOutcome(config, blockResults, counts).action;
}
/**
 * The same ladder, reporting WHICH SLOT matched as well as what it decided.
 *
 * One walk with two readers, deliberately: `resolveFlow` is implemented in terms of this rather
 * than beside it, because two copies of a first-match-wins ladder are two copies that drift --
 * and the slot and the action have to agree about the same submission or exit content shows
 * under a route it did not come from.
 */
export function resolveFlowOutcome(config, blockResults, counts = NO_COUNTS) {
    const hit = walkFlow(config, blockResults, counts).find((r) => r.matched && !r.drifted);
    if (hit)
        return { slot: hit.slot, action: hit.action };
    // Unreachable: the walk ends on `default`, which always matches. Restated rather than indexed
    // so a future rung list that somehow matched nothing still answers with the catch-all.
    return { slot: { kind: 'default' }, action: flowDefaultAction(config) };
}
/**
 * The ladder as a list, every rung carrying its own verdict.
 *
 * THE ONE WALK. `resolveFlow`, `resolveFlowOutcome` and `flowLadderOrder` are all readers of this
 * and none of them re-derives it, for the reason the outcome's own note gives: two copies of a
 * first-match-wins ladder are two copies that drift. A panel that drew its own copy would be a
 * third, and it would be the one a teacher believed.
 *
 * Every rung is evaluated rather than stopping at the first match, because the readers that want
 * the whole list want the near misses too. The conditions are small pure predicates over state
 * already in hand, so the extra work is a few array passes.
 */
function walkFlow(config, blockResults, counts) {
    const allFired = Object.values(blockResults).flat();
    /**
     * Two sensor rungs, no data before poor data.
     *
     * That order is the severity order and it matters when both fire at once: a student with one dead
     * sensor and one merely noisy one has a problem the retry rung cannot solve, so the redirect wins.
     *
     * Each rung is one action by default and per-block rows when split, first match winning within a
     * rung because two sensors can fail on one submission. The unmatched-but-failing fallback is not
     * decoration: a sensor block whose row has not been written yet (just added, config not
     * re-normalized) would otherwise fall past here to All Correct, which only checks for INCORRECT
     * and would happily advance a student whose compass never read. Staying put is never wrong.
     *
     * An unmatched rung still reports the slot and action it was AUTHORED with, so a reader drawing
     * the whole ladder can say where this rung would send a student without pretending it fired.
     */
    const sensorRung = (kind, rung, matches) => {
        const base = { matched: false, drifted: false };
        if (!allFired.some((s) => matches(s.trigger))) {
            return { ...base, slot: { kind }, action: rung?.action ?? { type: 'stay' } };
        }
        const fired = { matched: true, drifted: false };
        if (!rung)
            return { ...fired, slot: { kind }, action: { type: 'stay' } };
        if (!rung.split)
            return { ...fired, slot: { kind }, action: rung.action };
        const failed = (blockId) => (blockResults[blockId] ?? []).some((s) => matches(s.trigger));
        const row = (rung.rules ?? []).find((r) => failed(r.blockId));
        // The fallback keeps the RUNG's own slot: nothing was authored for a row that does not
        // exist, so there is no per-block slot for content to hang off either.
        return row
            ? { ...fired, slot: { kind, ref: row.blockId }, action: row.action }
            : { ...fired, slot: { kind }, action: { type: 'stay' } };
    };
    /*
     * EVERY BLOCK PASSED — see the ladder's note above for why this is positive rather than
     * negative.
     *
     * Asked of what FIRED, with no mode and no block data: the evaluator has already applied the
     * mode by choosing which state to return, so `isPositiveTrigger` is the whole test. A block
     * that fired nothing is absent from `blockResults` (see `fired-states`), so a display block
     * never has a vote and a content-only task is vacuously all-correct — which is the behaviour
     * it had under the negative test too.
     */
    const everyBlockPassed = Object.values(blockResults)
        .every((states) => states.some((s) => isPositiveTrigger(s.trigger)));
    const rungs = [
        {
            slot: { kind: 'noAnswer' },
            action: config.noAnswerAction,
            matched: allFired.some((s) => isIncompleteTrigger(s.trigger)),
            drifted: false,
        },
        sensorRung('noSensorData', config.noSensorData, isNoSensorDataTrigger),
        sensorRung('poorSensorData', config.poorSensorData, isPoorSensorDataTrigger),
        {
            slot: { kind: 'allCorrect' },
            action: config.allCorrectAction,
            matched: everyBlockPassed,
            drifted: false,
        },
    ];
    for (const rule of flowEventRules(config)) {
        rungs.push({
            slot: { kind: 'event', ref: rule.id },
            action: rule.action,
            matched: conditionsSatisfied(rule.conditions, rule.matchMode, blockResults, counts),
            drifted: hasDrifted(rule, blockResults),
            conditions: {
                /* Same reader as the match itself, so the tally cannot disagree with the verdict. */
                held: asConditionList(rule.conditions).filter((c) => conditionHolds(c, blockResults, counts)).length,
                total: asConditionList(rule.conditions).length,
                matchMode: rule.matchMode,
            },
        });
    }
    rungs.push({
        slot: { kind: 'default' },
        action: flowDefaultAction(config),
        matched: true,
        drifted: false,
    });
    return rungs;
}
/**
 * The SHAPE of the ladder: every rung in the order the engine tries them, with no submission in
 * hand.
 *
 * For a reader that has to lay the ladder out before anyone has answered anything. Order and
 * action are properties of the flow, not of a submission, so this cannot be wrong the way a
 * status read off an empty result map would be -- an empty map passes All Correct vacuously.
 *
 * `flowSlots` cannot answer this: it is a census for the orphan sweep and its order is the order
 * that was convenient to build, not the order anything is tried in. A split sensor rung reports
 * its RUNG-level action here, while the per-block rows it fans out to are slots of their own and
 * appear in `flowSlots` and in `taskConnections`.
 */
export function flowLadderOrder(config) {
    return walkFlow(config, {}, NO_COUNTS).map(({ slot, action }) => ({ slot, action }));
}
/**
 * Every slot this flow HAS, paired with whether it is currently an exit.
 *
 * The census the orphan prune reads: content filed against anything not in here, or against a
 * slot whose action is `stay`, can never be shown again.
 */
export function flowSlots(config) {
    const out = [
        { slot: { kind: 'noAnswer' }, action: config.noAnswerAction },
        { slot: { kind: 'allCorrect' }, action: config.allCorrectAction },
        { slot: { kind: 'default' }, action: flowDefaultAction(config) },
    ];
    for (const rule of flowEventRules(config)) {
        out.push({ slot: { kind: 'event', ref: rule.id }, action: rule.action });
    }
    for (const kind of ['noSensorData', 'poorSensorData']) {
        const rung = config[kind];
        if (!rung)
            continue;
        out.push({ slot: { kind }, action: rung.action });
        for (const row of rung.rules ?? [])
            out.push({ slot: { kind, ref: row.blockId }, action: row.action });
    }
    return out;
}
/**
 * Stored exit content that can never be shown again — a slot that is gone, or one that has been
 * switched back to `stay`.
 *
 * The `unusedAliases` of this feature, and swept the same way: on LEAVING the Flow panel rather
 * than on the keystroke, as its own undo step, and never while the task is dirty. Switch an
 * event to Stay and back inside one visit and nothing is lost.
 */
export function orphanExitSlots(config) {
    const live = new Set(flowSlots(config).filter((s) => isExitAction(s.action)).map((s) => flowSlotId(s.slot)));
    return Object.keys(config.exits ?? {}).filter((key) => !live.has(key));
}
/** The content authored for the slot a submission came out of, if any. */
export function exitContentFor(config, slot) {
    return config.exits?.[flowSlotId(slot)];
}
//# sourceMappingURL=flow.js.map